<template>
	<div class="resource-planning-user-select-dropdown">
		<!-- Loading data -->
		<SuiSkeleton
			v-if="selectedEntityType === TYPES.User && loading"
			is-circle
			width="4.25rem"
		/>
		<!-- Placeholder avatar for new role booking -->
		<SuiAvatar
			v-else-if="
				!isUserSelectionEnabled &&
				selectedEntityType === TYPES.RoleProject
			"
			v-tooltip="selectedEntityName"
			:aria-label="selectedEntityName"
			size="small"
			:color="PLACEHOLDER_BG_COLOR"
		>
			<SuiIcon name="icon-person-16" />
		</SuiAvatar>
		<!-- Enabled user selection for a role booking -->
		<SuiAvatar
			v-else-if="
				isUserSelectionEnabled &&
				selectedEntityType === TYPES.RoleProject
			"
			v-tooltip="$t('user_assign_select_add_user')"
			as="button"
			size="small"
			:color="PLACEHOLDER_BG_COLOR"
			:aria-label="$t('user_assign_select_add_user')"
			@click="toggleIsUserSelectOpen()"
		>
			<SuiIcon name="icon-addperson-16" />
		</SuiAvatar>
		<!-- Placeholder avatar for new user booking -->
		<SuiAvatar
			v-else-if="selectedUser && !isUserSelectionEnabled"
			v-tooltip="selectedUser.name"
			:url="selectedUser.profilePicture?.mini"
			:initials="selectedUser.initials || ''"
			:color="selectedUser.hslColor || ''"
			:aria-label="selectedUser.name"
			size="small"
			show-border
		/>
		<!-- Avatar for new user booking -->
		<SuiAvatar
			v-else-if="selectedUser"
			v-tooltip="$t('resource-booking__move-booking')"
			:url="selectedUser.profilePicture?.mini"
			:initials="selectedUser.initials || ''"
			:color="selectedUser.hslColor || ''"
			:aria-label="$t('resource-booking__move-booking')"
			size="small"
			show-border
			as="button"
			@click="toggleIsUserSelectOpen()"
		/>
		<VDropdown
			:shown="isUserSelectOpen"
			placement="bottom-start"
			auto-hide
			:triggers="[]"
			@auto-hide="onUserSelectClose"
		>
			<template #popper>
				<ResourcePlanningUserSelect
					:title="userSelectTitle"
					:exclude-user-id="
						selectedEntityType === TYPES.RoleProject
							? null
							: selectedUser?.id
					"
					@user-selected="$emit('user-selected', $event)"
					@close="onUserSelectClose"
				/>
			</template>
		</VDropdown>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, toRefs } from 'vue';
import { SuiIcon, SuiAvatar, SuiSkeleton } from '@developers/scoro-ui';
import { USER_LIST_USERS_INPUT } from './constants';
import { PLACEHOLDER_BG_COLOR } from '../../constants';
import { useToggle } from '@vueuse/core';
import ResourcePlanningUserSelect from './ResourcePlanningUserSelect.vue';
import {
	useResourcePlanningUserSelectQuery,
	type User,
	type Role,
	type RoleProject,
	TYPES,
} from '@/generated/graphql';
import { useTranslation } from '@/composables';

export type ResourcePlanningUserSelectDropdownProps = {
	isUserSelectionEnabled?: boolean;
	selectedEntityId?: User['id'] | RoleProject['id'];
	selectedEntityType?: typeof TYPES.User | typeof TYPES.RoleProject;
	selectedEntityName?: Role['name'];
};

const props = defineProps<ResourcePlanningUserSelectDropdownProps>();
const emit = defineEmits<{
	(event: 'user-selected', id: User['id']): void;
	(event: 'close'): void;
}>();

const { t } = useTranslation();
const { selectedEntityId, selectedEntityType } = toRefs(props);
const isOpen = ref(false);
const [isUserSelectOpen, toggleIsUserSelectOpen] = useToggle(isOpen.value);
const selectedUserId = computed(() =>
	selectedEntityType.value === TYPES.User ? selectedEntityId.value : null
);

const onUserSelectClose = () => {
	emit('close');

	isUserSelectOpen.value = false;
};

const { result, loading } = useResourcePlanningUserSelectQuery({
	usersListInput: USER_LIST_USERS_INPUT,
});

const selectedUser = computed(() =>
	result.value?.UsersList.find((user) => user.id === selectedUserId.value)
);

const userSelectTitle = computed(() =>
	props.selectedEntityType === TYPES.User
		? t('resource-booking__move-booking')
		: t('resource-booking__assign-member')
);
</script>
